import { writable } from 'svelte/store';
import { logWarning, logError } from '../logging/log-handler.js';

type Theme = 'light' | 'dark';
const DEFAULT_THEME: Theme = 'dark';

function getInitialTheme(): Theme {
    try {
        const stored = localStorage.getItem('theme');
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
        
        // Check system preference if no valid stored theme
        if (window.matchMedia('(prefers-color-scheme: light)').matches) {
            return 'light';
        }
    } catch (e) {
        // Handle localStorage or matchMedia errors
        logError(e, "ThemeStore", undefined, { message: "Could not access theme preferences" });
    }
    
    return DEFAULT_THEME;
}

function createThemeStore() {
    const { subscribe, set, update } = writable<Theme>(getInitialTheme());
    
    return {
        subscribe,
        set,
        update,
        /**
         * Reset theme to default (system preference or dark).
         * Called during cache cleanup - user's preference will be restored from database after login.
         */
        reset: () => {
            try {
                localStorage.removeItem('theme');
            } catch (_) {}
            // Use system preference or default
            const newTheme = window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : DEFAULT_THEME;
            set(newTheme);
        },
        /**
         * Set theme from user profile (after login).
         * This is called when user preferences are loaded from the database.
         */
        setFromProfile: (userTheme: Theme | undefined) => {
            if (userTheme === 'light' || userTheme === 'dark') {
                set(userTheme);
            }
        }
    };
}

export const theme = createThemeStore();

theme.subscribe(value => {
    try {
        localStorage.setItem('theme', value);
        document.body.classList.remove('light', 'dark');
        document.body.classList.add(value);
    } catch (e) {
        logError(e, "ThemeStore", undefined, { message: "Could not save theme preference" });
    }
});

// Listen for system theme changes
try {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    mediaQuery.addEventListener('change', e => {
        if (!localStorage.getItem('theme')) {  // Only update if no user preference
            theme.set(e.matches ? 'light' : 'dark');
        }
    });
} catch (e) {
    logError(e, "ThemeStore", undefined, { message: "Could not add system theme listener" });
}